import { useGetSettings, useUpdateSetting } from '../../services/api/settings.hooks'
import {
    getOptionsForSetting,
    getSelectedOption,
    getSelectedSortDirection,
    getSortAndFilterSetting,
} from './settingsContract'
import { Filter, SORT_DIRECTION, Sort, SortAndFilterSettings, SortAndFilterSettingsConfig } from './types'

// groupId is the id of the repo or task section (used when sorting views in the overview page)
const useSortAndFilterSettings = <T>(
    config: SortAndFilterSettingsConfig<T>,
    groupId?: string,
    suffix?: '_main' | '_overview', // main if a folder, overview if the overview page
    isEnabled = true
): SortAndFilterSettings<T> => {
    const { data: settings, isLoading } = useGetSettings(isEnabled)
    const { mutate: updateSetting } = useUpdateSetting()

    if (!isEnabled || isLoading || !settings) {
        return config.defaultSortsAndFilters
    }

    const sortingPreference = getSortAndFilterSetting(settings, config.settingsGroup, 'sort', groupId, suffix)
    const sortDirection = getSortAndFilterSetting(settings, config.settingsGroup, 'direction', groupId, suffix)
    const filterPreference = getSortAndFilterSetting(settings, config.settingsGroup, 'filter', groupId, suffix)

    const sortOptions = getOptionsForSetting(config.sortOptions, sortingPreference)
    const filterOptions = getOptionsForSetting(config.filterOptions, filterPreference)

    const selectedSort = getSelectedOption(sortOptions, sortingPreference, config.defaultSortsAndFilters.selectedSort)
    const setSelectedSort = (selectedSort: Sort<T>) => {
        if (sortingPreference) updateSetting({ key: sortingPreference.field_key, value: selectedSort.id })
    }
    const selectedSortDirection = getSelectedSortDirection(
        sortDirection,
        config.defaultSortsAndFilters.selectedSortDirection
    )
    const setSelectedSortDirection = (selectedSortDirection: SORT_DIRECTION) => {
        if (sortDirection) updateSetting({ key: sortDirection.field_key, value: selectedSortDirection })
    }
    const selectedFilter = getSelectedOption(
        filterOptions,
        filterPreference,
        config.defaultSortsAndFilters.selectedFilter
    )
    const setSelectedFilter = (selectedFilter: Filter<T>) => {
        if (filterPreference) updateSetting({ key: filterPreference.field_key, value: selectedFilter.id })
    }

    return {
        sortOptions,
        filterOptions,
        selectedSort,
        setSelectedSort,
        selectedSortDirection,
        setSelectedSortDirection,
        selectedFilter,
        setSelectedFilter,
        isLoading: false,
    }
}

export default useSortAndFilterSettings
